<template>
  <div class="sidebar-nav-demo">
    <!-- 🎯 Header -->
    <v-card class="mb-6" elevation="2">
      <v-card-title class="pa-6 bg-gradient-to-r from-blue-500 to-indigo-600 text-white">
        <div class="d-flex align-center">
          <v-icon icon="mdi-menu" size="32" class="me-3" />
          <div>
            <h2 class="text-h4 mb-0">🧭 SidebarNav Component Demo</h2>
            <p class="text-subtitle-1 mb-0 opacity-90">Navegación lateral adaptable por roles</p>
          </div>
        </div>
      </v-card-title>
    </v-card>

    <!-- 🔧 Demo Controls -->
    <v-card class="mb-4" elevation="1">
      <v-card-text class="pa-4">
        <v-row align="center">
          <v-col cols="12" md="3">
            <v-select
              v-model="selectedRole"
              :items="availableRoles"
              item-title="label"
              item-value="value"
              label="Simular Rol"
              variant="outlined"
              density="compact"
              prepend-inner-icon="mdi-account-switch"
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-switch
              v-model="sidebarExpanded"
              label="Sidebar Expandida"
              color="primary"
              hide-details
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-switch
              v-model="showIcons"
              label="Mostrar Iconos"
              color="primary"
              hide-details
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-switch
              v-model="darkMode"
              label="Modo Oscuro"
              color="primary"
              hide-details
            />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- 🧭 Sidebar Demo -->
    <v-row>
      <v-col cols="12" md="3">
        <v-card 
          :class="['sidebar-demo', { 'sidebar-dark': darkMode, 'sidebar-collapsed': !sidebarExpanded }]"
          elevation="2"
        >
          <v-card-title class="pa-4 bg-primary text-white">
            <div class="d-flex align-center">
              <v-icon 
                v-if="showIcons" 
                icon="mdi-view-dashboard" 
                class="me-2" 
              />
              <span v-if="sidebarExpanded">MGI Sistema</span>
            </div>
          </v-card-title>
          
          <v-list :class="{ 'py-0': !sidebarExpanded }" density="compact">
            <v-list-item
              v-for="item in filteredMenuItems"
              :key="item.id"
              :disabled="item.disabled"
              @click="selectMenuItem(item)"
            >
              <template #prepend v-if="showIcons">
                <v-icon :icon="item.icon" />
              </template>
              <v-list-item-title v-if="sidebarExpanded">{{ item.title }}</v-list-item-title>
              <template #append v-if="item.badge && sidebarExpanded">
                <v-badge
                  :content="item.badge"
                  :color="item.badgeColor || 'primary'"
                  inline
                />
              </template>
            </v-list-item>

            <v-divider v-if="sidebarExpanded" class="my-2" />
            
            <!-- Role-specific sections -->
            <template v-for="section in roleSpecificSections" :key="section.title">
              <v-list-subheader v-if="sidebarExpanded">{{ section.title }}</v-list-subheader>
              <v-list-item
                v-for="item in section.items"
                :key="item.id"
                @click="selectMenuItem(item)"
              >
                <template #prepend v-if="showIcons">
                  <v-icon :icon="item.icon" />
                </template>
                <v-list-item-title v-if="sidebarExpanded">{{ item.title }}</v-list-item-title>
              </v-list-item>
            </template>
          </v-list>
        </v-card>
      </v-col>

      <v-col cols="12" md="9">
        <v-card elevation="1">
          <v-card-title class="pa-4 bg-surface-variant">
            <v-icon icon="mdi-information" class="me-2" />
            Información del Menú Seleccionado
          </v-card-title>
          <v-card-text class="pa-4">
            <div v-if="selectedMenuItem">
              <h3 class="text-h6 mb-3">{{ selectedMenuItem.title }}</h3>
              <p class="text-body-2 mb-3">{{ selectedMenuItem.description }}</p>
              
              <v-chip-group>
                <v-chip
                  v-for="permission in selectedMenuItem.permissions"
                  :key="permission"
                  size="small"
                  variant="outlined"
                  color="primary"
                >
                  {{ permission }}
                </v-chip>
              </v-chip-group>

              <div class="mt-4">
                <h4 class="text-subtitle-1 mb-2">Detalles técnicos:</h4>
                <v-list density="compact">
                  <v-list-item>
                    <v-list-item-title>Ruta:</v-list-item-title>
                    <v-list-item-subtitle>{{ selectedMenuItem.route }}</v-list-item-subtitle>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-title>Componente:</v-list-item-title>
                    <v-list-item-subtitle>{{ selectedMenuItem.component }}</v-list-item-subtitle>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-title>Acceso requerido:</v-list-item-title>
                    <v-list-item-subtitle>{{ selectedMenuItem.requiredRole }}</v-list-item-subtitle>
                  </v-list-item>
                </v-list>
              </div>
            </div>
            <div v-else class="text-center pa-8">
              <v-icon icon="mdi-cursor-pointer" size="48" color="grey" class="mb-2" />
              <p class="text-subtitle-1">Selecciona un elemento del menú</p>
              <p class="text-caption text-medium-emphasis">Haz clic en cualquier opción del sidebar para ver sus detalles</p>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- 🔄 Back Navigation -->
    <v-card class="mt-6" elevation="1">
      <v-card-actions class="pa-4">
        <v-btn
          variant="outlined"
          prepend-icon="mdi-arrow-left"
          @click="$router.push('/dev-center/components')"
        >
          Volver a Componentes
        </v-btn>
        <v-spacer />
        <v-btn
          color="primary"
          variant="flat"
          prepend-icon="mdi-code-tags"
          @click="showCode"
        >
          Ver Código
        </v-btn>
      </v-card-actions>
    </v-card>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useMainStore } from '@/stores/main'

// 🎯 Types
interface MenuItem {
  id: string
  title: string
  description: string
  icon: string
  route: string
  component: string
  requiredRole: string
  permissions: string[]
  badge?: string
  badgeColor?: string
  disabled?: boolean
}

interface MenuSection {
  title: string
  items: MenuItem[]
}

// 🔧 Reactive state
const store = useMainStore()
const selectedRole = ref('super_admin')
const sidebarExpanded = ref(true)
const showIcons = ref(true)
const darkMode = ref(false)
const selectedMenuItem = ref<MenuItem | null>(null)

// 📊 Mock data
const availableRoles = ref([
  { label: 'Super Admin', value: 'super_admin' },
  { label: 'Gerente Regional', value: 'gerente_regional' },
  { label: 'Supervisor de Zona', value: 'supervisor_zona' },
  { label: 'Operador de Rutas', value: 'operador_rutas' },
  { label: 'Empleado de Agencia', value: 'empleado_agencia' }
])

const baseMenuItems = ref<MenuItem[]>([
  {
    id: 'dashboard',
    title: 'Dashboard',
    description: 'Panel principal con métricas y resumen',
    icon: 'mdi-view-dashboard',
    route: '/dashboard',
    component: 'DashboardView',
    requiredRole: 'any',
    permissions: ['read:dashboard'],
    badge: '3',
    badgeColor: 'success'
  },
  {
    id: 'incidents',
    title: 'Incidencias',
    description: 'Gestión de incidencias y reportes',
    icon: 'mdi-alert-circle',
    route: '/incidents',
    component: 'IncidentsView',
    requiredRole: 'any',
    permissions: ['read:incidents', 'create:incidents'],
    badge: '12',
    badgeColor: 'warning'
  },
  {
    id: 'routes',
    title: 'Rutas',
    description: 'Gestión y seguimiento de rutas',
    icon: 'mdi-map-marker-path',
    route: '/routes',
    component: 'RoutesView',
    requiredRole: 'operador_rutas',
    permissions: ['read:routes', 'update:routes']
  },
  {
    id: 'users',
    title: 'Usuarios',
    description: 'Administración de usuarios del sistema',
    icon: 'mdi-account-group',
    route: '/users',
    component: 'UsersView',
    requiredRole: 'super_admin',
    permissions: ['read:users', 'create:users', 'update:users', 'delete:users']
  },
  {
    id: 'reports',
    title: 'Reportes',
    description: 'Generación y visualización de reportes',
    icon: 'mdi-chart-line',
    route: '/reports',
    component: 'ReportsView',
    requiredRole: 'supervisor_zona',
    permissions: ['read:reports', 'generate:reports']
  }
])

const roleSpecificMenuItems = ref<Record<string, MenuSection[]>>({
  super_admin: [
    {
      title: 'Administración',
      items: [
        {
          id: 'system-config',
          title: 'Configuración',
          description: 'Configuración general del sistema',
          icon: 'mdi-cog',
          route: '/admin/config',
          component: 'SystemConfigView',
          requiredRole: 'super_admin',
          permissions: ['admin:config']
        },
        {
          id: 'audit-logs',
          title: 'Logs de Auditoría',
          description: 'Registro de actividades del sistema',
          icon: 'mdi-file-document',
          route: '/admin/audit',
          component: 'AuditLogsView',
          requiredRole: 'super_admin',
          permissions: ['admin:audit']
        }
      ]
    }
  ],
  gerente_regional: [
    {
      title: 'Gestión Regional',
      items: [
        {
          id: 'analytics',
          title: 'Analytics',
          description: 'Análisis y métricas regionales',
          icon: 'mdi-chart-box',
          route: '/manager/analytics',
          component: 'AnalyticsView',
          requiredRole: 'gerente_regional',
          permissions: ['read:analytics']
        },
        {
          id: 'staff-management',
          title: 'Gestión de Personal',
          description: 'Administración de equipos de trabajo',
          icon: 'mdi-account-supervisor',
          route: '/manager/staff',
          component: 'StaffManagementView',
          requiredRole: 'gerente_regional',
          permissions: ['manage:staff']
        }
      ]
    }
  ],
  operador_rutas: [
    {
      title: 'Operaciones',
      items: [
        {
          id: 'my-routes',
          title: 'Mis Rutas',
          description: 'Rutas asignadas y en progreso',
          icon: 'mdi-truck',
          route: '/operator/routes',
          component: 'MyRoutesView',
          requiredRole: 'operador_rutas',
          permissions: ['read:own_routes']
        },
        {
          id: 'field-reports',
          title: 'Reportes de Campo',
          description: 'Crear reportes desde el terreno',
          icon: 'mdi-clipboard-text',
          route: '/operator/reports',
          component: 'FieldReportsView',
          requiredRole: 'operador_rutas',
          permissions: ['create:field_reports']
        }
      ]
    }
  ]
})

// 🎯 Computed properties
const filteredMenuItems = computed(() => {
  if (selectedRole.value === 'super_admin') {
    return baseMenuItems.value
  }
  
  return baseMenuItems.value.filter(item => 
    item.requiredRole === 'any' || 
    item.requiredRole === selectedRole.value ||
    hasRolePermission(item.requiredRole)
  )
})

const roleSpecificSections = computed(() => {
  return roleSpecificMenuItems.value[selectedRole.value] || []
})

// 🎯 Methods
const hasRolePermission = (requiredRole: string) => {
  const roleHierarchy = {
    'super_admin': ['gerente_regional', 'supervisor_zona', 'operador_rutas', 'empleado_agencia'],
    'gerente_regional': ['supervisor_zona', 'operador_rutas', 'empleado_agencia'],
    'supervisor_zona': ['operador_rutas', 'empleado_agencia'],
    'operador_rutas': [],
    'empleado_agencia': []
  }
  
  return roleHierarchy[selectedRole.value as keyof typeof roleHierarchy]?.includes(requiredRole) || false
}

const selectMenuItem = (item: MenuItem) => {
  selectedMenuItem.value = item
  console.log('🧭 Elemento de menú seleccionado:', item.title)
  
  store.showNotification({
    type: 'info',
    title: 'Menú Seleccionado',
    message: `Has seleccionado: ${item.title}`
  })
}

const showCode = () => {
  console.log('💻 Mostrar código del componente SidebarNav')
  store.showNotification({
    type: 'info',
    title: 'Código del Componente',
    message: 'Abriendo vista del código fuente de SidebarNav...'
  })
}

// 🎬 Lifecycle
onMounted(() => {
  console.log('🧭 Demo de SidebarNav cargado')
  // Seleccionar el primer elemento por defecto
  if (filteredMenuItems.value.length > 0) {
    selectedMenuItem.value = filteredMenuItems.value[0]
  }
})
</script>

<style scoped>
.sidebar-nav-demo {
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px;
}

.sidebar-demo {
  transition: all 0.3s ease;
  min-height: 500px;
}

.sidebar-dark {
  background-color: #1a1a1a;
  color: white;
}

.sidebar-dark :deep(.v-list-item) {
  color: rgba(255, 255, 255, 0.87);
}

.sidebar-dark :deep(.v-list-item:hover) {
  background-color: rgba(255, 255, 255, 0.08);
}

.sidebar-collapsed {
  width: 80px;
}

.sidebar-collapsed :deep(.v-list-item-title) {
  display: none;
}

.sidebar-collapsed :deep(.v-list-subheader) {
  display: none;
}

.bg-gradient-to-r {
  background: linear-gradient(90deg, #2196f3 0%, #3f51b5 100%);
}

:deep(.v-list-item--active) {
  background-color: rgba(25, 118, 210, 0.12);
  color: #1976d2;
}

:deep(.v-list-item--active .v-icon) {
  color: #1976d2;
}
</style>
